import React, { useEffect, useState } from "react";
import ApexCharts from "apexcharts";
import jsonData from "../../../../current-survey-numeric.json";
import { FILTER_CONFIG } from "../../../../config/filterconfig"; // Import filter config
import "../../../../pages/D04_Responses_by_Demographic_Category.css";

const MultipleBarCharts = ({
  selectedQuestion,
  selectedDemographic,
  filterCriteria,
}) => {
  const [chartConfigs, setChartConfigs] = useState([]);
  const [demographicValues, setDemographicValues] = useState([]);

  useEffect(() => {
    // Find the config for the selected demographic field
    const demographicConfig = FILTER_CONFIG.find(
      (item) => item.fieldName === selectedDemographic || item.jsonKey === selectedDemographic
    );

    // Handle the case where config isn't found or is invalid
    if (!demographicConfig) {
      console.error(`No config found for demographic: ${selectedDemographic}`);
      setDemographicValues([]);
      setChartConfigs([]);
      return;
    }

    // Get the correct field to use from config
    const fieldToUse = demographicConfig.jsonKey || selectedDemographic;
    
    // Safely get the predefined sort order
    const predefinedSortOrder = demographicConfig.sortOrder || [];

    // Helper function to get sorted field values
    const getSortedFieldValues = (fieldName) => {
      // Ensure the field exists in the data
      if (!jsonData[0] || typeof jsonData[0][fieldName] === 'undefined') {
        console.error(`Field ${fieldName} not found in the data`);
        return [];
      }

      let values = [...new Set(jsonData.map((item) => item[fieldName]))].filter(
        Boolean
      );

      if (predefinedSortOrder && predefinedSortOrder.length > 0) {
        values = values.sort((a, b) => {
          const indexA = predefinedSortOrder.indexOf(a);
          const indexB = predefinedSortOrder.indexOf(b);

          if (indexA === -1 && indexB === -1) return a.localeCompare(b); // Alphabetical only if both are missing
          if (indexA === -1) return 1; // Push missing values to the end
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
      } else {
        values = values.sort((a, b) => a.localeCompare(b));
      }
      return values;
    };

    const demographicValues = getSortedFieldValues(fieldToUse);
    setDemographicValues(demographicValues);

    if (!demographicValues || demographicValues.length === 0) {
      setChartConfigs([]);
      return;
    }

    // Step 1: Calculate total counts for each category
    const totalCategoryCounts = {
      favorable: 0,
      neutral: 0,
      unfavorable: 0,
      noResponse: 0,
    };

    jsonData.forEach((response) => {
      const answer = response[selectedQuestion];
      if (answer === 4 || answer === 5) {
        totalCategoryCounts.favorable++;
      } else if (answer === 3) {
        totalCategoryCounts.neutral++;
      } else if (answer === 1 || answer === 2) {
        totalCategoryCounts.unfavorable++;
      } else {
        totalCategoryCounts.noResponse++;
      }
    });

    // Step 2: Calculate percentages for each demographic group relative to the total for each category
    const favorabilityData = demographicValues.reduce(
      (acc, value) => {
        const filteredData = filterDataByCriteria(
          jsonData,
          fieldToUse,
          value,
          filterCriteria
        );
        let favorable = 0;
        let neutral = 0;
        let unfavorable = 0;
        let noResponse = 0;

        filteredData.forEach((response) => {
          const answer = response[selectedQuestion];
          if (answer === 4 || answer === 5) {
            favorable++;
          } else if (answer === 3) {
            neutral++;
          } else if (answer === 1 || answer === 2) {
            unfavorable++;
          } else {
            noResponse++;
          }
        });

        // Calculate percentages against the total for each category, with safety checks
        acc.favorable.push(totalCategoryCounts.favorable > 0 ? (favorable / totalCategoryCounts.favorable) * 100 : 0);
        acc.neutral.push(totalCategoryCounts.neutral > 0 ? (neutral / totalCategoryCounts.neutral) * 100 : 0);
        acc.unfavorable.push(totalCategoryCounts.unfavorable > 0 ? (unfavorable / totalCategoryCounts.unfavorable) * 100 : 0);
        acc.noResponse.push(totalCategoryCounts.noResponse > 0 ? (noResponse / totalCategoryCounts.noResponse) * 100 : 0);

        return acc;
      },
      { favorable: [], neutral: [], unfavorable: [], noResponse: [] }
    );

    // Step 3: Prepare chart configurations for each category
    const configs = [
      {
        id: "bars-NR",
        title: "No Response",
        series: [{ name: "Percent", data: favorabilityData.noResponse }],
        colors: ["#bab0ac"],
      },
      {
        id: "bars-UF",
        title: "Unfavorable",
        series: [{ name: "Percent", data: favorabilityData.unfavorable }],
        colors: ["#f28e2b"],
      },
      {
        id: "bars-NT",
        title: "Neutral",
        series: [{ name: "Percent", data: favorabilityData.neutral }],
        colors: ["#f1ce63"],
      },
      {
        id: "bars-FV",
        title: "Favorable",
        series: [{ name: "Percent", data: favorabilityData.favorable }],
        colors: ["#499894"],
      },
    ];

    setChartConfigs(configs);
  }, [selectedQuestion, selectedDemographic, filterCriteria]);

  useEffect(() => {
    // Don't try to render charts if no data
    if (!demographicValues || demographicValues.length === 0 || !chartConfigs || chartConfigs.length === 0) {
      return;
    }

    const barHeight = 30;
    const chartHeight = Math.max(300, demographicValues.length * barHeight + 100); // Min height

    const charts = chartConfigs.map((config) => {
      const element = document.querySelector(`#${config.id}`);
      if (!element) {
        console.error(`Element with id ${config.id} not found`);
        return null;
      }

      const chart = new ApexCharts(element, {
        series: config.series,
        colors: config.colors,
        chart: {
          type: "bar",
          height: chartHeight,
          width: "100%",
          stacked: false,
        },
        legend: { show: false },
        plotOptions: {
          bar: {
            horizontal: true,
          },
        },
        title: {
          text: config.title,
          align: "center",
          style: { fontSize: "14px", fontFamily: "Ubuntu", color: "#3d3d3d" },
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => (isNaN(val) ? "0%" : `${val.toFixed(1)}%`),
        },
        stroke: {
          width: 1,
          colors: ["#fff"],
        },
        tooltip: {
          y: {
            formatter: (val) => (isNaN(val) ? "0%" : `${val.toFixed(1)}%`),
          },
        },
        xaxis: { categories: demographicValues }, // Ensure sorting is applied to x-axis
      });

      chart.render();
      return chart;
    });

    return () => {
      charts.forEach((chart) => {
        if (chart) chart.destroy();
      });
    };
  }, [chartConfigs, demographicValues]);

  const filterDataByCriteria = (
    data,
    demographicType,
    demographicValue,
    criteria
  ) => {
    return data.filter((item) => {
      if (!item[demographicType] || item[demographicType] === null)
        return false;
      let match = true;
      
      // Safely handle filter criteria
      if (criteria && typeof criteria === 'object') {
        Object.keys(criteria).forEach((key) => {
          if (
            criteria[key] && 
            Array.isArray(criteria[key]) && 
            criteria[key].length > 0 && 
            (!item[key] || !criteria[key].includes(item[key]))
          ) {
            match = false;
          }
        });
      }
      
      return match && item[demographicType] === demographicValue;
    });
  };

  return (
    <div className="D04_Responses_by_Demographic_Category-internal-charts">
      {chartConfigs.map((config) => (
        <div
          key={config.id}
          className="D04_Responses_by_Demographic_Category-internal-chart-container"
        >
          <div
            id={config.id}
            className="D04_Responses_by_Demographic_Category-internal-chart"
          ></div>
        </div>
      ))}
    </div>
  );
};

export default MultipleBarCharts;